import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Autocomplete, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createMaintenanceSchedule, type SaveMaintenanceSchedulePayload } from '../../api/mentenanta'
import { listEquipment } from '../../api/echipamente'
import { errorMessage } from '../../api/client'

const schema = z.object({
  equipmentId: z.string().min(1, 'Selectați echipamentul'),
  interventionType: z.string().min(1, 'Tipul de intervenție este obligatoriu'),
  intervalType: z.enum(['Zilnic', 'Saptamanal', 'Lunar', 'Trimestrial', 'Semestrial', 'Anual']),
  lastServiceDate: z.string().min(1, 'Câmp obligatoriu'),
  preaviceDays: z.string().min(1, 'Câmp obligatoriu'),
  escalationDays: z.string().min(1, 'Câmp obligatoriu'),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  equipmentId: '', interventionType: '', intervalType: 'Lunar',
  lastServiceDate: new Date().toISOString().slice(0, 10), preaviceDays: '7', escalationDays: '3',
}

export default function MaintenanceScheduleFormDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: equipment } = useQuery({ queryKey: ['mentenanta', 'echipamente', 'utilaje'], queryFn: listEquipment, enabled: open })
  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })

  const mutation = useMutation({
    mutationFn: (v: FormValues) => createMaintenanceSchedule({
      equipmentId: v.equipmentId, interventionType: v.interventionType, intervalType: v.intervalType,
      lastServiceDate: v.lastServiceDate, preaviceDays: Number(v.preaviceDays), escalationDays: Number(v.escalationDays),
    } satisfies SaveMaintenanceSchedulePayload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mentenanta', 'calendar'] })
      reset(EMPTY)
      onClose()
    },
  })

  const handleClose = () => { mutation.reset(); reset(EMPTY); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>Calendar de mentenanță nou</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="equipmentId" control={control} render={({ field }) => (
              <Autocomplete
                options={equipment?.map((e) => e.id) ?? []}
                getOptionLabel={(id) => { const e = equipment?.find((x) => x.id === id); return e ? `${e.code} — ${e.name}` : id }}
                value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                renderInput={(params) => <TextField {...params} label="Echipament" error={!!errors.equipmentId} helperText={errors.equipmentId?.message} />}
              />
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="interventionType" control={control} render={({ field }) => (
                <TextField {...field} label="Tip intervenție" sx={{ flexGrow: 1 }} placeholder="ex. Revizie lunară"
                  error={!!errors.interventionType} helperText={errors.interventionType?.message} />
              )} />
              <Controller name="intervalType" control={control} render={({ field }) => (
                <TextField {...field} select label="Interval" sx={{ width: 160 }} error={!!errors.intervalType} helperText={errors.intervalType?.message}>
                  <MenuItem value="Zilnic">Zilnic</MenuItem>
                  <MenuItem value="Saptamanal">Săptămânal</MenuItem>
                  <MenuItem value="Lunar">Lunar</MenuItem>
                  <MenuItem value="Trimestrial">Trimestrial</MenuItem>
                  <MenuItem value="Semestrial">Semestrial</MenuItem>
                  <MenuItem value="Anual">Anual</MenuItem>
                </TextField>
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="lastServiceDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Ultima intervenție" sx={{ width: 190 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.lastServiceDate} helperText={errors.lastServiceDate?.message} />
              )} />
              <Controller name="preaviceDays" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Preaviz (zile)" sx={{ width: 150 }} error={!!errors.preaviceDays} helperText={errors.preaviceDays?.message} />
              )} />
              <Controller name="escalationDays" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Escaladare după (zile)" sx={{ width: 190 }} error={!!errors.escalationDays} helperText={errors.escalationDays?.message} />
              )} />
            </Stack>
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClose}>Anulează</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
